import { useState } from 'react';
import { Button, Card, Space, Divider, Typography } from 'antd';
import { PrinterOutlined, FileTextOutlined, CodeOutlined } from '@ant-design/icons';
import { PrintPreview, openPrintPreview } from '../../components/Print';
import moment from 'moment';

const { Title, Paragraph, Text } = Typography;

/**
 * PrintPreviewPage
 * This is the "Blank Page" requested by the user.
 * It shows an example form/data and provides options to print.
 */
const PrintPreviewPage = () => {
    const [showInPage, setShowInPage] = useState(false);

    // Common Header Configuration
    const commonHeaderConfig = {
        hospitalName: 'Prakruti Ayurvedic Health Center & Hospital',
        subtitle: 'A Project By Prakruti Ayurvedic Health Resort, Satara',
        address: 'Satara, Maharashtra, India',
        phone: '+91 9175242908',
        email: 'feelbetter@prakruti.health',
        website: 'www.prakruti.health',
        logo: '/prakruti-logo.png',
    };

    // Example JSON Data
    const exampleJsonData = {
        documentId: "PR-2026-8890",
        generatedDate: moment().format('YYYY-MM-DD HH:mm:ss'),
        status: "Verified",
        patientDetails: {
            name: "Rahul Sharma",
            age: 38,
            gender: "Male",
            registrationNo: "REG-00123"
        },
        assessment: {
            temperature: "98.4 F",
            pulse: "72 bpm",
            bloodPressure: "120/80",
            weight: "74 kg"
        },
        notes: "Patient is recovering well. Advised to continue current medications."
    };

    // Structured Sections for Print
    const structuredSections = [
        {
            title: 'Patient Information',
            fields: [
                { label: 'Name', value: exampleJsonData.patientDetails.name },
                { label: 'Age/Gender', value: `${exampleJsonData.patientDetails.age} / ${exampleJsonData.patientDetails.gender}` },
                { label: 'Registration No', value: exampleJsonData.patientDetails.registrationNo },
                { label: 'Status', value: exampleJsonData.status },
            ],
        },
        {
            title: 'Clinical Assessment',
            fields: [
                { label: 'Temperature', value: exampleJsonData.assessment.temperature },
                { label: 'Pulse', value: exampleJsonData.assessment.pulse },
                { label: 'Blood Pressure', value: exampleJsonData.assessment.bloodPressure },
                { label: 'Weight', value: exampleJsonData.assessment.weight },
            ],
        },
        {
            title: 'Medical Notes',
            fields: [
                { label: 'Remarks', value: exampleJsonData.notes },
            ],
        }
    ];

    const handleOpenNewTab = () => {
        openPrintPreview(
            exampleJsonData,
            'Example Medical Report',
            {
                sections: structuredSections,
                headerConfig: commonHeaderConfig
            }
        );
    };

    return (
        <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
            <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div>
                        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                            <FileTextOutlined /> Common Print Component
                        </Title>
                        <Paragraph type="secondary" style={{ marginTop: '8px' }}>
                            A multi-purpose component for printing JSON data with a consistent brand header.
                        </Paragraph>
                    </div>
                    <Space size="middle">
                        <Button
                            type="primary"
                            size="large"
                            icon={<PrinterOutlined />}
                            onClick={handleOpenNewTab}
                            style={{ borderRadius: '8px' }}
                        >
                            Print in New Tab
                        </Button>
                        <Button
                            size="large"
                            icon={<CodeOutlined />}
                            onClick={() => setShowInPage(!showInPage)}
                            style={{ borderRadius: '8px' }}
                        >
                            {showInPage ? 'Hide Preview' : 'Show In-Page Preview'}
                        </Button>
                    </Space>
                </div>

                <Divider />

                <div className="row">
                    <div className="col-md-6">
                        <Title level={4}>Example JSON Data</Title>
                        <Card
                            style={{ background: '#1e1e1e', borderRadius: '8px', border: 'none' }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <pre style={{ color: '#d4d4d4', margin: 0, fontSize: '13px', overflow: 'auto' }}>
                                {JSON.stringify(exampleJsonData, null, 2)}
                            </pre>
                        </Card>

                        <div style={{ marginTop: '24px' }}>
                            <Title level={4}>Integration Guide</Title>
                            <Paragraph>
                                To use this component in any modal or page:
                            </Paragraph>
                            <Card
                                size="small"
                                style={{ background: '#fafafa', border: '1px solid #e8e8e8' }}
                            >
                                <Text code>{"import { openPrintPreview } from '../../components/Print';"}</Text>
                                <br /><br />
                                <Text code>{"const handlePrint = () => {"}</Text><br />
                                <Text code>{"  openPrintPreview(jsonData, 'Title', { sections });"}</Text><br />
                                <Text code>{"};"}</Text>
                            </Card>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <Title level={4}>Example Structured Format</Title>
                        <Card bordered={false} style={{ background: '#fff', borderLeft: '4px solid #1890ff' }}>
                            {structuredSections.map((section, idx) => (
                                <div key={idx} style={{ marginBottom: '16px' }}>
                                    <Text strong style={{ color: '#1890ff' }}>{section.title}</Text>
                                    <div style={{ paddingLeft: '12px', marginTop: '8px' }}>
                                        {section.fields.map((field, fIdx) => (
                                            <div key={fIdx} style={{ display: 'flex', marginBottom: '4px' }}>
                                                <Text type="secondary" style={{ width: '120px' }}>{field.label}:</Text>
                                                <Text>{field.value}</Text>
                                            </div>
                                        ))}
                                    </div>
                                    {idx < structuredSections.length - 1 && <Divider style={{ margin: '12px 0' }} />}
                                </div>
                            ))}
                        </Card>
                    </div>
                </div>

                {showInPage && (
                    <div style={{ marginTop: '32px' }}>
                        <Title level={4}>In-Page Live Preview</Title>
                        <div style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px',
                            padding: '24px',
                            background: '#fff',
                            boxShadow: 'inset 0 0 10px rgba(0,0,0,0.05)'
                        }}>
                            <PrintPreview
                                data={exampleJsonData}
                                title="In-Page Preview Mode"
                                sections={structuredSections}
                                headerConfig={commonHeaderConfig}
                                showCloseButton={false}
                            />
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default PrintPreviewPage;
